/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
import { getTimeString } from '../../utils/time';
import { balanceMinutes } from '../../utils/pto';

const DATE_OPTIONS = {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
};

const PILL = {
  Submitted: { bg: '#fde6d2', fg: '#F19235' },
  Approved: { bg: '#eef5ea', fg: '#53A551' },
  Denied: { bg: '#fdebe6', fg: '#b94f3c' },
};

function StatusPill({ status }) {
  const c = PILL[status] || PILL.Denied;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full"
      style={{
        backgroundColor: c.bg, color: c.fg, fontSize: '11px', fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
}

StatusPill.propTypes = { status: PropTypes.string.isRequired };

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-ink-500 font-medium mb-1">{label}</p>
      {children}
    </div>
  );
}

Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

function RequestViewModal({
  policies, balances, selectedRequest, closeModal,
}) {
  const start = new Date(selectedRequest.startDate);
  const end = new Date(selectedRequest.endDate);
  const selectedPolicy = policies?.find((item) => item.id === selectedRequest.policyId);
  const requested = selectedRequest.totalHours;
  const balance = selectedPolicy && balances ? balances[selectedPolicy.id] : null;

  let currentBalance = getTimeString(0);
  let remaining = getTimeString(0 - requested);
  if (balance) {
    const currentBalanceValue = Math.trunc(balanceMinutes(balance));
    currentBalance = getTimeString(currentBalanceValue);
    remaining = getTimeString(currentBalanceValue - requested);
  }

  const requestedStr = getTimeString(selectedRequest.totalHours);
  const isSubmitted = selectedRequest.status === 'Submitted';
  const isDenied = selectedRequest.status === 'Denied';

  return (
    <Modal open onClose={() => closeModal(false)} className="flex-row align-c jstfy-c">
      <Box style={{
        width: 672,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
      >
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-0">Time Off Request</h5>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-6">
          <div className="space-y-4">
            <Field label="Policy">
              <p className="text-sm text-ink-900 font-medium mb-0">{selectedPolicy?.name || '—'}</p>
            </Field>
            <Field label="Time Span">
              <p className="text-sm text-ink-900 font-medium mb-0">
                {start.toLocaleDateString('en-US', DATE_OPTIONS)}
                {' '}
                to
                {' '}
                {end.toLocaleDateString('en-US', DATE_OPTIONS)}
              </p>
            </Field>
            <Field label="Total Time Off Hours">
              <p className="text-sm text-ink-900 font-medium tabular-nums mb-0">{requestedStr}</p>
            </Field>
            <Field label={isDenied ? 'Reason for Denial' : 'Time Off Reason'}>
              <p className="text-sm text-ink-900 font-medium mb-0">{isDenied ? selectedRequest.denyReason : selectedRequest.reason}</p>
            </Field>
            <Field label="Status">
              <StatusPill status={selectedRequest.status} />
            </Field>
          </div>

          <div className="border-l border-surface-1 pl-6 space-y-4">
            {isSubmitted ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-500">Current Balance</span>
                  <span className="text-ink-900 font-medium tabular-nums">{currentBalance}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-500">Hours Requested</span>
                  <span className="text-ink-900 font-medium tabular-nums">{requestedStr}</span>
                </div>
                <div className="border-t border-surface-1 pt-3 flex items-center justify-between text-sm">
                  <span className="text-ink-500">Remaining Balance</span>
                  <span className="text-ink-900 font-semibold tabular-nums">{remaining}</span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink-500">Hours Requested</span>
                <span className="text-ink-900 font-medium tabular-nums">{requestedStr}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-start items-center gap-2">
          <button
            type="button"
            onClick={() => closeModal(false)}
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
          >
            Close
          </button>
        </div>
      </Box>
    </Modal>
  );
}

RequestViewModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  policies: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  balances: PropTypes.object,
  selectedRequest: PropTypes.shape({
    id: PropTypes.number,
    totalHours: PropTypes.number,
    policyId: PropTypes.number,
    status: PropTypes.string,
    reason: PropTypes.string,
    denyReason: PropTypes.string,
    allDay: PropTypes.bool,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
};

RequestViewModal.defaultProps = {
  policies: [],
  balances: {},
};

export default RequestViewModal;
